import i18n from "../i18n";
import useReaderStore from "../context/readerStore";
import {
  createNote as createNoteApi,
  deleteNote as deleteNoteApi,
  fetchAccessData,
  getSessionTokens,
  getValidRstToken,
  updateNote as updateNoteApi,
} from "./igpService";
import {
  showOperationFailedNotification,
  showSessionExpiredNotification,
} from "./notificationService";

/**
 * Note service using IGP API as source-of-truth with OPTIMISTIC UPDATES.
 * LocalStorage usage removed — notes are stored in the global `useReaderStore`.
 *
 * Optimistic Update Pattern:
 * 1. Update store immediately with optimistic data (temp ID)
 * 2. Close UI immediately (no blocking)
 * 3. Async API call in background with error handling + rollback
 */

export async function refreshNotesFromApi() {
  try {
    const { grantId, agtToken } = getSessionTokens();
    const rstToken = await getValidRstToken(grantId, agtToken);
    const accessData = await fetchAccessData(grantId, rstToken, ["notes"]);
    // API returns notes directly under `notes.data` in the returned `data` object
    const notes = accessData?.notes?.data || [];
    // Update the global store
    const { setAnnotationData } = useReaderStore.getState();
    setAnnotationData("notes", notes);
    return notes;
  } catch (err) {
    console.error("Error refreshing notes from API:", err);
    if (err.response?.status === 401) {
      showSessionExpiredNotification();
    }
    return [];
  }
}

/**
 * Internal: Persist note to API in background with error handling.
 * Removes from store on error.
 */
async function _persistNoteToApi(
  operation,
  tempNoteId,
  noteData,
  noteId = null,
) {
  try {
    const { grantId, agtToken } = getSessionTokens();
    const rstToken = await getValidRstToken(grantId, agtToken);

    if (operation === "create") {
      await createNoteApi(grantId, rstToken, noteData);
    } else if (operation === "update") {
      await updateNoteApi(grantId, rstToken, noteId, noteData);
    } else if (operation === "delete") {
      await deleteNoteApi(grantId, rstToken, noteId);
    }

    // Refresh notes list to sync real IDs and get updated data
    await refreshNotesFromApi();
  } catch (err) {
    console.error(`Error persisting note (${operation}):`, err);

    // Rollback: Remove from store on error
    const state = useReaderStore.getState();
    const filteredNotes = state.annotations.notes.data.filter(
      (n) => n.id !== tempNoteId,
    );
    state.setAnnotationData("notes", filteredNotes);

    // Show error notification
    if (err.response?.status === 401) {
      showSessionExpiredNotification();
    } else {
      showOperationFailedNotification(
        operation === "create"
          ? i18n.t("notifications.operations.saveNote")
          : operation === "update"
            ? i18n.t("notifications.operations.updateNote")
            : i18n.t("notifications.operations.deleteNote"),
      );
    }
  }
}

/**
 * Create note with OPTIMISTIC UPDATE.
 * Returns immediately with optimistic note.
 * API persists in background.
 */
export function createNote(noteData, optimisticNoteId = null) {
  const state = useReaderStore.getState();
  const { annotations, setAnnotationData } = state;

  // Generate temporary ID if not provided
  const tempId =
    optimisticNoteId ||
    `note_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

  // Create optimistic note object
  const optimisticNote = {
    ...noteData,
    id: tempId,
    timestamp: noteData.timestamp || new Date().toISOString(),
    _isPending: true, // Mark as optimistic for UI
  };

  // Add to store immediately (optimistic update)
  const updatedNotes = [...annotations.notes.data, optimisticNote];
  setAnnotationData("notes", updatedNotes);

  // Fire API call in background - non-blocking
  _persistNoteToApi("create", tempId, noteData);

  // Return immediately - don't wait for API
  return { id: tempId, ...optimisticNote };
}

/**
 * Edit note with OPTIMISTIC UPDATE.
 * Returns immediately with updated note.
 * API persists in background.
 */
export function editNote(noteId, updateData, tempNoteId = null) {
  const state = useReaderStore.getState();
  const { annotations, setAnnotationData } = state;

  // Find and update the note in store
  const updatedNotes = annotations.notes.data.map((note) => {
    if (note.id === noteId) {
      return {
        ...note,
        ...updateData,
        _isPending: true, // Mark as optimistic for UI
      };
    }
    return note;
  });

  // Update store immediately (optimistic update)
  setAnnotationData("notes", updatedNotes);

  // Fire API call in background - non-blocking
  _persistNoteToApi("update", tempNoteId || noteId, updateData, noteId);

  // Return immediately - don't wait for API
  const updatedNote = updatedNotes.find((n) => n.id === noteId);
  return updatedNote;
}

/**
 * Delete note with OPTIMISTIC UPDATE.
 * Removes from store immediately.
 * API deletion happens in background with rollback on error.
 */
export function removeNoteById(noteId) {
  const state = useReaderStore.getState();
  const { annotations, setAnnotationData } = state;

  // Save the note in case we need to rollback
  const noteToDelete = annotations.notes.data.find((n) => n.id === noteId);

  // Remove from store immediately (optimistic update)
  const updatedNotes = annotations.notes.data.filter((n) => n.id !== noteId);
  setAnnotationData("notes", updatedNotes);

  // Fire API call in background - non-blocking
  // If it fails, _persistNoteToApi will rollback by removing from store
  // but since we already removed it, we need custom error handling here
  _persistNoteToApiDelete(noteId, noteToDelete);

  // Return immediately - don't wait for API
  return noteToDelete;
}

/**
 * Internal: Special handling for delete with enhanced rollback.
 */
async function _persistNoteToApiDelete(noteId, noteData) {
  try {
    const { grantId, agtToken } = getSessionTokens();
    const rstToken = await getValidRstToken(grantId, agtToken);
    await deleteNoteApi(grantId, rstToken, noteId);
    // Refresh notes list after successful delete
    await refreshNotesFromApi();
  } catch (err) {
    console.error("Error deleting note:", err);

    // Rollback: Re-add the note to store
    const state = useReaderStore.getState();
    const { annotations, setAnnotationData } = state;
    const updatedNotes = [...annotations.notes.data, noteData];
    setAnnotationData("notes", updatedNotes);

    // Show error notification
    if (err.response?.status === 401) {
      showSessionExpiredNotification();
    } else {
      showOperationFailedNotification(i18n.t("notifications.operations.deleteNote"));
    }
  }
}

export default useReaderStore;
